import { i18n, translateSessionStatus, useTranslation } from '@eon/i18n';
import { Card, CardContent, CardHeader, PageHeader, StatCard } from '@eon/ui';
import { hooks } from '@eon/api-client';
import { Activity, Cable, Building2, Map, Users, Zap, ChevronRight } from 'lucide-react';
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDate, formatDateTime, formatKWh } from '@eon/utils';
import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';

const FLEET_COLORS = [
  'hsl(142,71%,45%)',
  'hsl(188,95%,48%)',
  'hsl(0,72%,52%)',
  'hsl(220,14%,46%)',
];

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className="bg-primary h-3.5 w-0.5 rounded-full" />
      <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-foreground/80">
        {children}
      </h3>
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = hooks.useAdminDashboard();
  const counts = hooks.useAdminDeviceCounts();
  const sessions = hooks.useAdminSessions({ pageSize: 5 });
  const lang = i18n.language;

  const deviceCounts = data?.deviceCounts ?? counts.data;
  const energyChartData = useMemo(() => {
    if (!data?.energyByHour?.length) return [];
    return data.energyByHour.map((p) => ({
      hour: formatDate(p.hour, 'HH:mm', lang),
      kwh: p.kWh,
    }));
  }, [data?.energyByHour, lang]);

  const fleetData = deviceCounts
    ? [
        {
          name: t('dashboard.onlineDevices'),
          value: Math.max(0, deviceCounts.online - deviceCounts.charging),
        },
        { name: t('dashboard.chargingNow'), value: deviceCounts.charging },
        { name: t('dashboard.faulted'), value: deviceCounts.faulted },
        { name: t('dashboard.offline'), value: deviceCounts.offline },
      ]
    : [];

  return (
    <>
      <PageHeader title={t('nav.dashboard')} description={t('dashboard.description')} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('dashboard.users')}
          value={isLoading ? '—' : (data?.users ?? 0)}
          subtext={
            isLoading
              ? undefined
              : t('dashboard.usersSubtext', {
                  charging: data?.usersChargingNow ?? 0,
                  total: data?.users ?? 0,
                })
          }
          icon={Users}
        />
        <StatCard
          label={t('dashboard.devices')}
          value={isLoading ? '—' : (data?.devices ?? 0)}
          subtext={
            deviceCounts
              ? t('dashboard.devicesSubtext', {
                  available: deviceCounts.available,
                  charging: deviceCounts.charging,
                  offline: deviceCounts.offline,
                })
              : undefined
          }
          icon={Cable}
        />
        <StatCard
          label={t('dashboard.stations')}
          value={isLoading ? '—' : (data?.stations ?? 0)}
          subtext={
            isLoading ? undefined : t('dashboard.stationsSubtext', { total: data?.stations ?? 0 })
          }
          icon={Building2}
        />
        <StatCard
          label={t('dashboard.activeSessions')}
          value={isLoading ? '—' : (data?.sessionsTotal ?? 0)}
          subtext={
            isLoading
              ? undefined
              : t('dashboard.sessionsSubtext', {
                  active: data?.sessionsActive ?? 0,
                  total: data?.sessionsTotal ?? 0,
                })
          }
          hint={
            data?.energyKWh24h
              ? `${formatKWh(data.energyKWh24h)} · ${t('dashboard.last24h')}`
              : undefined
          }
          icon={Activity}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <SectionTitle>{t('dashboard.energyChart')}</SectionTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {energyChartData.length === 0 ? (
                <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                  {isLoading ? t('common.loading') : t('common.noData')}
                </div>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={energyChartData} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                  <defs>
                    <linearGradient id="energyGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(188,95%,48%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(188,95%,48%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="hour"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    interval={3}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1 }}
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 10,
                      fontSize: 12,
                    }}
                    labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="kwh"
                    stroke="hsl(188,95%,48%)"
                    strokeWidth={2}
                    fill="url(#energyGrad)"
                    dot={false}
                    activeDot={{ r: 4, fill: 'hsl(188,95%,48%)' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <SectionTitle>{t('dashboard.fleetHealth')}</SectionTitle>
          </CardHeader>
          <CardContent>
            {deviceCounts ? (
              <div className="flex flex-col items-center gap-4">
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={fleetData}
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={68}
                        paddingAngle={3}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {fleetData.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={FLEET_COLORS[index % FLEET_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: 10,
                          fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid w-full grid-cols-2 gap-2">
                  {fleetData.map((item, i) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ background: FLEET_COLORS[i % FLEET_COLORS.length] }}
                      />
                      <span className="text-muted-foreground truncate text-xs">{item.name}</span>
                      <span className="ml-auto text-xs font-semibold">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex h-40 items-center justify-center">
                <span className="text-muted-foreground text-sm">{t('common.loading')}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <SectionTitle>{t('dashboard.latestSessions')}</SectionTitle>
          </CardHeader>
          <CardContent>
            {sessions.data?.data && sessions.data.data.length > 0 ? (
              <div className="divide-y divide-border/50">
                {sessions.data.data.slice(0, 5).map((s, idx) => (
                  <div key={s.id ?? idx} className="flex items-center gap-3 py-3">
                    <div className="bg-primary/10 grid h-8 w-8 shrink-0 place-items-center rounded-lg">
                      <Zap className="text-primary h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-xs font-medium text-foreground">
                        {(s.id ?? '—').slice(0, 8).toUpperCase()}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {formatDateTime(s.startedAt, lang)}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        String(s.status) === 'charging'
                          ? 'bg-energy-500/15 text-energy-500'
                          : ['stopped', 'completed', 'finishing'].includes(String(s.status))
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {translateSessionStatus(t, String(s.status))}
                    </span>
                    <span className="text-xs font-semibold text-foreground">
                      {s.totalEnergyKWh != null ? formatKWh(s.totalEnergyKWh) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted-foreground py-8 text-center text-sm">
                {sessions.isLoading ? t('common.loading') : t('common.noData')}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <SectionTitle>{t('dashboard.quickActions')}</SectionTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { label: t('actions.remoteStart'), icon: Zap, to: '/devices' },
                { label: t('actions.viewMap'), icon: Map, to: '/map' },
                { label: t('actions.exportReport'), icon: Activity, to: '/sessions' },
              ].map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => void navigate({ to: action.to })}
                  className="group flex w-full items-center gap-3 rounded-xl border border-border/50 bg-muted/30 px-4 py-3.5 text-left transition-all hover:border-primary/30 hover:bg-primary/5 hover:shadow-glow"
                >
                  <div className="bg-primary/10 grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors group-hover:bg-primary/15">
                    <action.icon className="text-primary h-4 w-4" />
                  </div>
                  <span className="flex-1 text-sm font-medium text-foreground">{action.label}</span>
                  <ChevronRight className="text-muted-foreground h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
